
import * as React from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement;

      // Small delay to allow the modal to render and transitions to start before focusing
      const timer = setTimeout(() => {
        if (modalRef.current) {
          const focusableElementsSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
          const focusableElements = modalRef.current.querySelectorAll(focusableElementsSelector);
          const firstFocusableElement = focusableElements[0] as HTMLElement;

          if (firstFocusableElement) {
            firstFocusableElement.focus();
          } else {
            // If no focusable elements, focus the modal container itself
            modalRef.current.focus();
          }
        }
      }, 100);

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
          return;
        }

        if (event.key === 'Tab') {
          if (!modalRef.current) return;

          const focusableElementsSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
          const focusableElements = Array.from(
            modalRef.current.querySelectorAll(focusableElementsSelector)
          ) as HTMLElement[];
          
          if (focusableElements.length === 0) {
            event.preventDefault();
            return;
          }
          
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (event.shiftKey) { // Shift + Tab
            if (document.activeElement === firstElement) {
              lastElement.focus();
              event.preventDefault();
            }
          } else { // Tab
            if (document.activeElement === lastElement) {
              firstElement.focus();
              event.preventDefault();
            }
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKeyDown);
        previouslyFocusedElement.current?.focus();
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-3 sm:p-6"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div
        ref={modalRef}
        tabIndex={-1} // Make it programmatically focusable
        className="w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col focus:outline-none border border-white/10 shadow-2xl rounded-2xl sm:rounded-[32px] overflow-hidden bg-[#12121a]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-4 sm:px-6 py-4 sm:py-5 border-b border-white/10 bg-white/5">
          <h2 className="text-lg sm:text-2xl font-semibold text-white pr-2 line-clamp-2">{title}</h2>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-slate-400 hover:text-white rounded-full p-2 transition-all duration-200 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 sm:p-8 overflow-y-auto bg-[#12121a]">
          {children}
        </div>
      </div>
    </div>
  );
};
